import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import i18n from '../i18n/'
import analytics from '../analytics'

Vue.use(Vuex)

const LOGIN_PENDING = 'LOGIN_PENDING'
const LOGIN_SUCCESS = 'LOGIN_SUCCESS'
const LOGIN_FAILURE = 'LOGIN_FAILURE'
const LOGOUT = 'LOGOUT'

const CREATE_ACCOUNT_PENDING = 'CREATE_ACCOUNT_PENDING'
const CREATE_ACCOUNT_SUCCESS = 'CREATE_ACCOUNT_SUCCESS'
const CREATE_ACCOUNT_FAILURE = 'CREATE_ACCOUNT_FAILURE'

const SET_PENDING_QUESTIONS = 'SET_PENDING_QUESTIONS'
const SET_PENDING_QUESTIONS_LOADING = 'SET_PENDING_QUESTIONS_LOADING'
const SET_ACCOUNT = 'SET_ACCOUNT'
const SET_USER = 'SET_USER'
const SET_USER_LANGUAGE = 'SET_USER_LANGUAGE'
const SET_LANGUAGE = 'SET_LANGUAGE'
const SET_TOKEN = 'SET_TOKEN'

const PRELOAD_PENDING = 'PRELOAD_PENDING'
const PRELOAD_SUCCESS = 'PRELOAD_SUCCESS'
const PRELOAD_FAILURE = 'PRELOAD_FAILURE'

const TRANSLATIONS_LOADING = 'TRANSLATIONS_LOADING'

const CLEAR_SESSION = 'CLEAR_SESSION'

const SET_CHALLENGE_CATEGORIES = 'SET_CHALLENGE_CATEGORIES'
const SET_CHALLENGE_THEME = 'SET_CHALLENGE_THEME'
const SET_CHALLENGE = 'CHALLENGE'
const SET_JOURNAL = 'SET_JOURNAL'
const JOURNAL_LOADING = 'JOURNAL_LOADING'
const ADD_JOURNAL_ENTRY = 'ADD_JOURNAL_ENTRY'
const REMOVE_JOURNAL_ENTRY = 'REMOVE_JOURNAL_ENTRY'
const EDIT_JOURNAL_ENTRY = 'EDIT_JOURNAL_ENTRY'
const PAGE_ENTRY = 'PAGE_ENTRY'
const SET_CHALLENGE_PAGE_JOURNAL_ENTRIES = 'SET_CHALLENGE_PAGE_JOURNAL_ENTRIES'

const SET_PERSONAL_ANXIETY_REPORT = 'SET_PERSONAL_ANXIETY_REPORT'
const SET_PERSONAL_PSYCHOSOCIAL_WORKLOAD_REPORT = 'SET_PERSONAL_PSYCHOSOCIAL_WORKLOAD_REPORT'
const SET_TEAM_ANXIETY_REPORT = 'SET_TEAM_ANXIETY_REPORT'

// Services
const loadAccount = () => {
  return new Promise((resolve, reject) => {
    axios.get('/account').then((response) => {
      resolve(response.data.account)
    }).catch((err) => {
      reject(err)
    })
  })
}

const store = {
  state: function () {
    const isLoggedIn = localStorage.getItem('token')
    if (isLoggedIn) {
      axios.defaults.headers.common.Authorization = 'Bearer ' + isLoggedIn
    }

    const baseState = {
      isLoggedIn: isLoggedIn,
      loginPending: false,
      loginFailure: false,
      preloadPending: false,
      preloadSuccess: false,
      preloadFailure: false,
      createAccountPending: false,
      createAccountFailure: false,
      user: {},
      language: i18n.i18next.language,
      pendingQuestions: null,
      pendingQuestionsLoading: null,
      account: {},
      challengeCategories: { fi: null, en: null },
      theme: {},
      challenge: {},
      journal: [],
      journalLoading: false,
      translationsLoading: true,
      challengePageJournalEntries: null,
      personalAnxietyReport: null,
      personalPsychosocialWorkloadReport: null,
      teamAnxietyReport: null
    }

    if (i18n.i18next.language === undefined) {
      baseState.language = 'en'
    }

    return baseState
  },

  mutations: {
    [CLEAR_SESSION] (state) {
      localStorage.removeItem('token')
      axios.defaults.headers.common.Authorization = undefined
      state.isLoggedIn = false
      state.loginPending = false
      state.loginFailure = false
      state.preloadPending = false
      state.preloadSuccess = false
      state.preloadFailure = false
      state.createAccountPending = false
      state.createAccountFailure = false
      state.user = {}
      store.challenge = {}
      state.pendingQuestions = null
      state.pendingQuestionsLoading = null
      state.account = {}
      state.challengeCategories = { fi: null, en: null }
      state.journal = []
      state.journalLoading = false
    },
    [LOGIN_PENDING] (state) {
      state.loginPending = true
      state.loginFailure = false
      state.isLoggedIn = false
    },
    [LOGIN_SUCCESS] (state, { token, user }) {
      localStorage.setItem('token', token)
      axios.defaults.headers.common.Authorization = 'Bearer ' + token
      state.user = user
      state.isLoggedIn = token
      state.loginPending = false
      state.loginFailure = false
    },
    [LOGIN_FAILURE] (state) {
      state.isLoggedIn = false
      state.loginPending = false
      state.loginFailure = true
    },
    [PRELOAD_PENDING] (state) {
      state.preloadPending = true
      state.preloadFailure = false
      state.preloadSuccess = false
    },
    [PRELOAD_SUCCESS] (state) {
      state.preloadPending = false
      state.preloadFailure = false
      state.preloadSuccess = true
    },
    [PRELOAD_FAILURE] (state) {
      state.preloadSuccess = false
      state.preloadPending = false
      state.preloadFailure = true
    },
    [TRANSLATIONS_LOADING] (state, loadingStatus) {
      state.translationsLoading = loadingStatus
    },
    [CREATE_ACCOUNT_PENDING] (state) {
      state.createAccountPending = true
      state.createAccountFailure = false
    },
    [CREATE_ACCOUNT_SUCCESS] (state, { token, user, account }) {
      localStorage.setItem('token', token)
      axios.defaults.headers.common.Authorization = 'Bearer ' + token
      state.user = user
      state.account = account
      state.isLoggedIn = true
      state.createAccountPending = false
      state.createAccountFailure = false
    },
    [CREATE_ACCOUNT_FAILURE] (state) {
      state.createAccountPending = false
      state.createAccountFailure = true
    },
    [LOGOUT] (state) {
      state.isLoggedIn = false
      state.user = {}
      state.pendingQuestions = null
      state.account = {}
    },
    [SET_PENDING_QUESTIONS] (state, { questions }) {
      state.pendingQuestions = questions
    },
    [SET_PENDING_QUESTIONS_LOADING] (state, { loading }) {
      state.pendingQuestionsLoading = loading
    },
    [SET_ACCOUNT] (state, { account }) {
      state.account = account
      if (state.user && state.user._id && state.account && state.account._id) {
        analytics.identify(state.user._id, {
          $name: state.user.name,
          $email: state.user.email,
          $created: state.user.createdAt,
          language: state.user.language,
          type: state.user.type,
          validated: state.user.validated,
          emooterUpdates: state.user.userSettings.emooterUpdates,
          personalReports: state.user.userSettings.personalReports,
          wellbeingCheckUps: state.user.userSettings.wellbeingCheckUps,
          workCommunityReports: state.user.userSettings.workCommunityReports,
          accountId: state.account._id,
          accountName: state.account.name
        })
      }
    },
    [SET_USER] (state, { user }) {
      state.user = user
      if (state.user.language) {
        i18n.i18next.changeLanguage(state.user.language)
      }
    },
    [SET_USER_LANGUAGE] (state, { language }) {
      state.user.language = language
    },
    [SET_LANGUAGE] (state, { language }) {
      state.language = language
    },
    [SET_TOKEN] (state, { token }) {
      localStorage.setItem('token', token)
      axios.defaults.headers.common.Authorization = 'Bearer ' + token
      state.isLoggedIn = true
      state.loginPending = false
      state.loginFailure = false
    },
    [SET_CHALLENGE_CATEGORIES]: (state, { challengeCategories, language }) => {
      state.challengeCategories[language] = challengeCategories
    },
    [SET_CHALLENGE_THEME]: (state, { theme }) => {
      state.theme = theme
    },
    [SET_CHALLENGE]: (state, { challenge }) => {
      state.challenge = challenge
    },
    [SET_JOURNAL]: (state, { journal }) => {
      state.journal = journal
    },
    [JOURNAL_LOADING]: (state, { loading }) => {
      state.journalLoading = loading
    },
    [ADD_JOURNAL_ENTRY]: (state, { entry }) => {
      state.journal.unshift(entry)
    },
    [REMOVE_JOURNAL_ENTRY]: (state, { entry }) => {
      const updatedJournal = state.journal.filter(item => {
        return item._id !== entry._id
      })
      state.journal = updatedJournal
    },
    [EDIT_JOURNAL_ENTRY]: (state, { entry }) => {
      const index = state.journal.findIndex(item => {
        return item._id === entry._id
      })
      state.journal.splice(index, 1, entry)
    },
    [PAGE_ENTRY]: (state, { currentpage, themeId, challengeId }) => {
      state.currentpage = currentpage
      state.themeId = themeId
      state.challengeId = challengeId
    },
    [SET_CHALLENGE_PAGE_JOURNAL_ENTRIES]: (state, { content }) => {
      state.challengePageJournalEntries = content
    },
    [SET_PERSONAL_ANXIETY_REPORT]: (state, { report }) => {
      state.personalAnxietyReport = report
    },
    [SET_PERSONAL_PSYCHOSOCIAL_WORKLOAD_REPORT]: (state, { report }) => {
      state.personalPsychosocialWorkloadReport = report
    },
    [SET_TEAM_ANXIETY_REPORT]: (state, { report }) => {
      state.teamAnxietyReport = report
    }
  },
  actions: {
    createAccount ({ state, commit, dispatch }, newAccount) {
      return new Promise((resolve, reject) => {
        axios.post('account', newAccount).then((response) => {
          commit(CREATE_ACCOUNT_SUCCESS, { token: response.data.token, user: response.data.user, account: response.data.account })
          dispatch('preloadState')
          resolve()
        }).catch((err) => {
          commit(CREATE_ACCOUNT_FAILURE)
          reject(err)
        })
      })
    },
    tokenLogin ({ commit, dispatch, state }, token) {
      commit(CLEAR_SESSION)
      commit(LOGIN_PENDING)
      return new Promise((resolve, reject) => {
        commit(SET_TOKEN, token) // SET TOKEN FOR AXIOS, WILL FAIL IF SERVER DOESN'T ACCEPT IT
        dispatch('preloadState').then(() => {
          analytics.track('tokenLogin', { })
          resolve()
        }).catch((err) => {
          analytics.track('tokenLoginFailure', { })
          commit(LOGIN_FAILURE)
          reject(err)
        })
      })
    },
    login ({
      state,
      commit,
      dispatch,
      rootState
    }, credentials) {
      commit(CLEAR_SESSION)
      commit(LOGIN_PENDING)
      return new Promise((resolve, reject) => {
        axios.post('auth', credentials).then((response) => {
          analytics.track('manualLogin', { })
          commit(LOGIN_SUCCESS, { token: response.data.token, user: response.data.user })
          dispatch('preloadState')
          resolve()
        }).catch((err) => {
          analytics.track('manualLoginFailure', { })
          commit(LOGIN_FAILURE)
          reject(err)
        })
      })
    },
    logout ({ commit }) {
      localStorage.removeItem('token')
      commit(LOGOUT)
    },
    updatePreferredLanguage ({ commit, state }, language) {
      return new Promise((resolve, reject) => {
        commit(SET_LANGUAGE, { language: language })
        if (!state.user._id || (state.user.language && state.user.language === language)) {
          resolve()
        } else {
          commit(SET_USER_LANGUAGE, { language: language })
          axios.post(`users/${state.user._id}`, { user: state.user }).then(response => {
            resolve()
          }).catch(err => {
            reject(err)
          })
        }
      })
    },
    updateUser ({ commit }, user) {
      commit(SET_USER, { user: user })
    },
    loadUser ({ commit, state, dispatch }) {
      return new Promise((resolve, reject) => {
        axios.get('auth').then(response => {
          commit(SET_USER, { user: response.data.user })
          if (!state.user.language) {
            dispatch('updatePreferredLanguage', i18n.i18next.language)
          }
          resolve()
        }).catch(err => {
          commit(CLEAR_SESSION)
          reject(err)
        })
      })
    },
    loadAccount ({ commit, state }) {
      return new Promise((resolve, reject) => {
        loadAccount().then((account) => {
          commit(SET_ACCOUNT, { account: account })
          resolve()
        }).catch((err) => {
          reject(err)
        })
      })
    },
    validateAccount ({ commit, dispatch }, token) {
      return new Promise((resolve, reject) => {
        axios.post('users/validate', { token: token }).then((response) => {
          commit(CLEAR_SESSION)
          commit(LOGIN_SUCCESS, { token: response.data.token, user: response.data.user })
          dispatch('preloadState')
          resolve(response)
        }).catch((err) => {
          reject(err)
        })
      })
    },
    loadPendingQuestions ({ commit }) {
      return new Promise((resolve, reject) => {
        commit(SET_PENDING_QUESTIONS_LOADING, { loading: true })
        axios.get('/scale/pending').then((response) => {
          commit(SET_PENDING_QUESTIONS, { questions: response.data.questions })
          // .sort((a, b) => { return a.scaleIdentifier > b.scaleIdentifier ? 1 : (b.scaleIdentifier > a.scaleIdentifier ? -1 : 0) }) })
          commit(SET_PENDING_QUESTIONS_LOADING, { loading: false })
          resolve()
        }).catch((err) => {
          commit(SET_PENDING_QUESTIONS_LOADING, { loading: false })
          commit(SET_PENDING_QUESTIONS, { questions: [] })
          reject(err)
        })
      })
    },
    preloadState ({ state, commit, dispatch }) {
      if (!state.preloadPending && state.isLoggedIn !== false) {
        commit(PRELOAD_PENDING)
        return new Promise((resolve, reject) => {
          dispatch('loadUser').then(() => {
            Promise.all([
              dispatch('loadAccount'),
              dispatch('loadPendingQuestions')
            ]).then(() => {
              commit(PRELOAD_SUCCESS)
              resolve()
            }).catch((err) => {
              commit(PRELOAD_FAILURE)
              dispatch('logout')
              reject(err)
            })
          }).catch((err) => {
            commit(PRELOAD_FAILURE)
            dispatch('logout')
            reject(err)
          })
        })
      }
    },
    loadChallengeCategories ({ commit, state, dispatch }) {
      return new Promise((resolve, reject) => {
        const language = state.language
        if (state.challengeCategories[language] === null) {
          axios.get(`/private/content/challenges/?lang=${language}`)
            .then((response) => {
              commit(SET_CHALLENGE_CATEGORIES, { challengeCategories: response.data.categories, language: language })
              resolve()
            }).catch((err) => {
              reject(err)
            })
        } else {
          resolve()
        }
      })
    },
    // loadChallengeTheme ({ commit, state }, id) {
    //   axios.get('/private/content/themes/' + id + '/?lang=' + state.language).then((response) => {
    //     commit(SET_CHALLENGE_THEME, { theme: response.data.theme })
    //   })
    // },
    loadChallenge ({ commit, state }, id) {
      axios.get('/private/content/challenges/' + id + '/?lang=' + state.language).then((response) => {
        commit(SET_CHALLENGE, { challenge: response.data.challenge })
      })
    },
    setTranslationsLoading ({ commit }, loadingStatus) {
      commit(TRANSLATIONS_LOADING, loadingStatus)
    },
    loadJournal ({ commit }) {
      commit(JOURNAL_LOADING, { loading: true })
      axios.get('/journal').then((response) => {
        commit(SET_JOURNAL, { journal: response.data.entries })
        commit(JOURNAL_LOADING, { loading: false })
      }).catch(() => {
        commit(JOURNAL_LOADING, { loading: false })
      })
    },
    loadChallengePageJournalEntries ({ commit }, challengePageReference) {
      return new Promise((resolve, reject) => {
        // commit(JOURNAL_LOADING, { loading: true })
        axios.get('/journal/challenge/' + challengePageReference.challengeId + '/' + challengePageReference.challengePageId).then((response) => {
          commit(SET_CHALLENGE_PAGE_JOURNAL_ENTRIES, { content: response.data.pageEntry })
          resolve()
        }).catch((err) => {
          reject(err)
        })
      })
    },
    addJournalEntry ({ commit, dispatch }, newJournalEntry) {
      return new Promise((resolve, reject) => {
        axios.post('/journal', { entry: newJournalEntry }).then((response) => {
          commit(ADD_JOURNAL_ENTRY, { entry: response.data.entry })
          analytics.track('addJournalEntry', { })
          resolve()
        }).catch((err) => {
          dispatch('loadJournal')
          reject(err)
        })
      })
    },
    removeEntry ({ commit, dispatch }, id) {
      return new Promise((resolve, reject) => {
        axios.delete('/journal/' + id).then((response) => {
          commit(REMOVE_JOURNAL_ENTRY, { entry: response.data.entry })
          resolve()
        }).catch((err) => {
          dispatch('loadJournal')
          reject(err)
        })
      })
    },
    editJournalEntry ({ commit, dispatch }, entry) {
      return new Promise((resolve, reject) => {
        axios.put('/journal/' + entry.id, { content: entry.content }).then((response) => {
          commit(EDIT_JOURNAL_ENTRY, { entry: response.data.entry })
          resolve()
        }).catch((err) => {
          dispatch('loadJournal')
          reject(err)
        })
      })
    },
    loadPersonalAnxietyReport ({ commit }) {
      return new Promise((resolve, reject) => {
        axios.get('/stats/user/current/report/anxiety-gad-7').then((response) => {
          if (response.data.report) {
            commit(SET_PERSONAL_ANXIETY_REPORT, { report: response.data.report })
            resolve()
          } else {
            reject(new Error('No data'))
          }
        }).catch((err) => {
          reject(err)
        })
      })
    },
    loadPersonalPsychosocialWorkloadReport ({ commit }) {
      return new Promise((resolve, reject) => {
        axios.get('/stats/user/current/report/psychosocial-workload').then((response) => {
          if (response.data.report) {
            commit(SET_PERSONAL_PSYCHOSOCIAL_WORKLOAD_REPORT, { report: response.data.report })
            resolve()
          } else {
            reject(new Error('No data'))
          }
        }).catch((err) => {
          reject(err)
        })
      })
    },
    loadTeamAnxietyReport ({ commit }) {
      return new Promise((resolve, reject) => {
        axios.get('/stats/account/current/report/anxiety-gad-7').then((response) => {
          if (response.data.report) {
            commit(SET_TEAM_ANXIETY_REPORT, { report: response.data.report })
            resolve()
          } else {
            reject(new Error('No data'))
          }
        }).catch((err) => {
          reject(err)
        })
      })
    }
  },
  getters: {
    isLoggedIn: state => {
      return state.isLoggedIn
    },
    isLoginPending: state => state.loginPending,
    isLoginFailure: state => state.loginFailure,
    user: state => state.user,
    account: state => state.account,
    numberOfPendingQuestions: state => state.pendingQuestions ? state.pendingQuestions.length : 0,
    pendingQuestionsLoading: state => state.pendingQuestionsLoading,
    isOwner: state => {
      if (state.account && state.account.owners && state.user) {
        return state.account.owners.indexOf(state.user._id) >= 0
      }
      return false
    },
    accountUsers: state => state.account.users,
    theme: state => state.theme,
    challengeCategories: state => state.challengeCategories[state.language],
    challenge: state => state.challenge,
    journal: state => state.journal,
    journalLoading: state => state.journalLoading,
    language: state => state.language,
    translationsLoading: state => state.translationsLoading,
    challengePageJournalEntries: state => state.challengePageJournalEntries,
    personalAnxietyReport: state => state.personalAnxietyReport,
    personalPsychosocialWorkloadReport: state => state.personalPsychosocialWorkloadReport,
    teamAnxietyReport: state => state.teamAnxietyReport
  }
}

export default new Vuex.Store(store)
